import { Box } from '@jfs/ui-new'
import { FC, ReactElement, useCallback, useEffect, useState } from 'react'
import * as React from 'react'
import ReactDOM from 'react-dom'
import { Dimensions } from 'react-native'
import { actionMenuStrings } from '../../../src/data/static/strings'
import { CalendarTypes } from '../../comps/Calendar'
import CalendarViewModal from '../../comps/CalendarViewModal'
import { ModBox } from '../../comps/ModifiedBox/ModifiedBox'
import Tabbar, { TabbarType, TabData } from '../../comps/Tabbar'
import { calendarWebWidth, getRangeDate, getYesterday } from '../../util/util'
import { styles } from './styles'

const stats = [
  { title: 'Today' },
  { title: 'Yesterday' },
  { title: 'This Week' },
  { title: 'This Month' },
  { title: 'Custom Dates', iconId: 'CALENDAR' },
]

interface DateTabsProps {
  onDateSelector: (fromDate: Date, toDate: Date, dateLabel: string) => void
  disabled?: boolean
  maxRange?: number
  sendError?: any
}

const DateTabs: FC<DateTabsProps> = (props) => {
  const { onDateSelector, disabled, maxRange, sendError } = props
  const [tabbarData, setTabbarData] = useState<TabData[]>(stats)
  const [index, setIndex] = useState(0)
  const [visible, setVisible] = useState(false)
  const [touchPos, updateTouchPos] = useState({})
  const windowWidth = Dimensions.get('window').width
  const calendarLayoutWdith = 699
  const leftPos = touchPos?.clientX - touchPos?.offsetX
  const remainingWidth = windowWidth - calendarLayoutWdith
  const isWebCalendarOpenInNative = windowWidth - calendarWebWidth < 200

  const [date, setDate] = useState<{ fromDate: Date; toDate: Date }>({ fromDate: new Date(), toDate: new Date() })

  useEffect(() => {
    const todayMidnight = new Date(new Date().setHours(0, 0, 0, 0))
    setTabbarData(stats)
    onDateSelector(todayMidnight, new Date(Date.now()), stats.slice()[index]?.title)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getDateValue = (value: number): Date => {
    const today = new Date(Date.now())
    return new Date(today.setDate(today.getDate() - value))
  }

  const getYesterdayValue = useCallback(() => {
    const yesterdayBegin = getYesterday('START')
    const yesterdayEnd = getYesterday('END')
    setDate({
      fromDate: yesterdayBegin,
      toDate: yesterdayEnd,
    })
    onDateSelector(yesterdayBegin, yesterdayEnd, actionMenuStrings.yesterday)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getSelectedDate = useCallback((type: number): Date => {
    const todayMidnight = new Date(new Date().setHours(0, 0, 0, 0))
    const getSelectedValue: any = {
      0: todayMidnight,
      1: getYesterdayValue(),
      2: getDateValue(7),
      3: getDateValue(30),
    }
    return getSelectedValue[type]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectedHandler = (_index: number) => {
    if (_index === 4) {
      setVisible(true)
      return
    } else if (_index === 1) {
      setIndex(_index)
      getSelectedDate(_index)
      return
    } else {
      const today = new Date(Date.now())
      const currentDay = today
      const finishDay = getSelectedDate(_index)
      setDate({
        fromDate: finishDay,
        toDate: currentDay,
      })
      onDateSelector(finishDay, currentDay, stats.slice()[_index]?.title)
      setIndex(_index)
      // setTabbarData(stats)
    }
  }

  const handleDateSelected = () => {
    setVisible(false)
  }

  const onDateSelected = (firstdate: Date, secondDate?: Date): void => {
    if (secondDate) {
      setDate({ fromDate: firstdate, toDate: secondDate })
      const dateString = getRangeDate(firstdate, secondDate)
      const updatedStatistics = stats.slice()
      const items: TabData[] = []
      updatedStatistics.map((item) => {
        if (item.title === 'Custom Dates') {
          items.push({ title: dateString, iconId: item.iconId })
        } else {
          items.push(item)
        }
        return item
      })
      setTabbarData(items)
      setIndex(4)
      onDateSelector(firstdate, secondDate, actionMenuStrings.customDate)
      setVisible(false)
    }
  }

  const onClose = (): void => {
    setVisible(false)
  }

  const returnLeftValue = () => {
    if (isWebCalendarOpenInNative) {
      return undefined
    } else if (!isWebCalendarOpenInNative && remainingWidth > leftPos) {
      return leftPos
    } else {
      return undefined
    }
  }

  const returnRightValue = () => {
    if (remainingWidth < leftPos || isWebCalendarOpenInNative) {
      return 17
    } else {
      return undefined
    }
  }

  const renderCalendarModal = (): ReactElement => {
    const handleModalPress = (): void => {
      handleDateSelected()
    }

    return (
      <ModBox {...styles().modal} show={visible}>
        <Box underlayColor="TRANSPARENT" onPress={handleModalPress} {...styles().overlayStyle}>
          <Box {...styles().dropdownStyle(returnLeftValue(), touchPos?.y ?? 0, returnRightValue())}>
            <CalendarViewModal
              onDateSelection={onDateSelected}
              dateRange={getRangeDate(date.fromDate, date.toDate)}
              type={CalendarTypes.HISTORY}
              onCancel={onClose}
              maxDuration={maxRange}
              sendError={sendError}
            />
          </Box>
        </Box>
      </ModBox>
    )
  }

  const renderPortal = (): React.ReactPortal => {
    return ReactDOM.createPortal(renderCalendarModal(), document.body)
  }

  const onLayout = (data: any) => {
    updateTouchPos(data?.nativeEvent)
  }

  return (
    <Box>
      <Tabbar
        default={index}
        titles={tabbarData}
        onSelected={selectedHandler}
        type={TabbarType.highlights}
        disabled={disabled}
        onLayout={onLayout}
      />
      {renderPortal()}
    </Box>
  )
}

export { DateTabs }
